import { useState, useTransition } from 'react';
import { Link } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { ChartTypes, DonutChart } from '@givebutter/primitives/chart';
import { maskDecimal } from '@givebutter/util';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HouseholdGivingSummaryContact, fetchHouseholdChartStats } from 'api/households';
import * as DropdownMenu from 'components/dropdown-menu';
import { DateOption } from 'constants/households';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContactHousehold } from 'customHooks/useCurrentContactHousehold';
import useDebounce from 'customHooks/useDebounce';
import { transformCurrencyToCompactNumberFormat } from 'helpers/formatting';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';

type DateOptions = {
    [key in DateOption]: {
        type: DateOption;
        buttonLabel: string;
        emptyLabel: string;
        topDonorLabel: string;
    };
};

const ContactHouseholdChart = () => {
    const [isPendingInternal, startTransition] = useTransition();
    const isPending = useDebounce(isPendingInternal, 500);
    const { accountId } = useCurrentAccount();
    const { data: household } = useCurrentContactHousehold();
    const currencySymbol = useCurrencySymbol();

    const dateOptions: DateOptions = {
        [DateOption.YearToDate]: {
            type: DateOption.YearToDate,
            buttonLabel: 'Year to date',
            emptyLabel: 'This household has not given this year.',
            topDonorLabel: 'of households this year',
        },
        [DateOption.LastYear]: {
            type: DateOption.LastYear,
            buttonLabel: 'Last year',
            emptyLabel: 'This household did not give last year.',
            topDonorLabel: 'of households last year',
        },
        [DateOption.AllTime]: {
            type: DateOption.AllTime,
            buttonLabel: 'All time',
            emptyLabel: 'This household has yet to give.',
            topDonorLabel: 'of all households',
        },
    };

    const [selectedDateOption, setSelectedDateOption] = useState<DateOption>(DateOption.YearToDate);

    const {
        data = {
            chartData: [],
            donation_count: 0,
            average_donation: 0,
            most_recent_donation: null,
            largest_donation: null,
            total_donated: 0,
            contacts: [],
        },
    } = useSuspenseQuery({
        queryKey: accountQueryKeys.householdChart(accountId, household.id, selectedDateOption),
        queryFn: () => fetchHouseholdChartStats(accountId, household.id, selectedDateOption),
        select: (
            data,
        ): {
            chartData: ChartTypes.ChartData<{ name: string; amount: number }>;
            donation_count: number;
            average_donation: number;
            total_donated: number;
            most_recent_donation: { id: number; amount: number } | null;
            largest_donation: { id: number; amount: number } | null;
            contacts: HouseholdGivingSummaryContact[];
        } => {
            const contactsWithTotalAmounts =
                household.contacts
                    .map(contact => {
                        const totalAmount = data.transactions
                            .filter(transaction => transaction.contact_id === contact.id)
                            .reduce((sum, transaction) => sum + transaction.amount, 0);

                        return { ...contact, total_donated: totalAmount };
                    })
                    .filter(contact => {
                        return contact.total_donated > 0;
                    }) ?? [];

            const chartData = contactsWithTotalAmounts.map(payload => ({
                name: `${payload.first_name} ${payload.last_name}`,
                amount: Math.max(
                    (payload.total_donated / data.aggregates_by_date_range.total) * 100,
                    1,
                ),
            }));

            return {
                chartData,
                donation_count: data.transactions.length,
                average_donation: data.aggregates_by_date_range.total / data.transactions.length,
                total_donated: data.aggregates_by_date_range.total,
                most_recent_donation: data.transactions[data.transactions.length - 1],
                largest_donation: data.aggregates_by_date_range.largest_donation,
                contacts: contactsWithTotalAmounts,
            };
        },
    });

    return (
        <Box className={twJoin(isPending && 'is-loading')}>
            <div className="flex flex-col gap-1">
                <div className={data.donation_count > 0 ? 'lg:grid lg:grid-cols-2' : ''}>
                    <div className="flex flex-col gap-2 p-4">
                        <div className="flex items-center justify-between gap-4 pb-0">
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger>
                                    <div className="flex justify-between">
                                        <div className="flex items-center gap-1 whitespace-nowrap text-sm text-gray-600">
                                            {dateOptions[selectedDateOption].buttonLabel}
                                            <FontAwesomeIcon icon={faChevronDown} />
                                        </div>
                                    </div>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content>
                                    {Object.values(DateOption).map(val => (
                                        <DropdownMenu.Item
                                            key={val}
                                            onClick={() => {
                                                startTransition(() => setSelectedDateOption(val));
                                            }}
                                        >
                                            {dateOptions[val].buttonLabel}
                                        </DropdownMenu.Item>
                                    ))}
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                        {!data.donation_count && (
                            <div className="mt-3 flex flex-grow flex-col items-center justify-center bg-gray-50 p-4 px-4 py-8 text-center text-sm">
                                <div className="font-medium text-gray-600">
                                    {dateOptions[selectedDateOption].emptyLabel}
                                </div>
                                {selectedDateOption !== DateOption.LastYear && (
                                    <div className="text-gray-500">
                                        Reach out and encourage support!
                                    </div>
                                )}
                            </div>
                        )}
                        {data.donation_count > 0 && (
                            <div className={'flex flex-col items-center justify-center'}>
                                <DonutChart.Root
                                    index={'name'}
                                    value={'amount'}
                                    data={data.chartData}
                                    valueFormatter={value => `${maskDecimal(value)}%`}
                                >
                                    <DonutChart.Content>
                                        <DonutChart.Tooltip />
                                        <DonutChart.Title>
                                            <div className={'flex flex-col items-center'}>
                                                <p className="text-sm text-gray-600">
                                                    Contributions
                                                </p>
                                                <p className="text-xl font-medium">
                                                    {currencySymbol}
                                                    {transformCurrencyToCompactNumberFormat(
                                                        data.total_donated,
                                                        99999,
                                                    )}
                                                </p>
                                            </div>
                                        </DonutChart.Title>
                                    </DonutChart.Content>
                                    <DonutChart.Legend className={'flex-col'} />
                                </DonutChart.Root>
                            </div>
                        )}
                    </div>

                    {data.donation_count > 0 && (
                        <div className="grid grid-cols-2 gap-6 p-6 lg:border-l lg:border-gray-200">
                            <div className="flex flex-col gap-8">
                                <div>
                                    <div className="text-sm text-gray-600">Total donations</div>
                                    <div className="text-base font-medium">
                                        {data.donation_count > 0 ? data.donation_count : '—'}
                                    </div>
                                </div>
                                <div className="group">
                                    <div className="text-sm text-gray-600">Most recent</div>
                                    <div className="text-base font-medium">
                                        {data.most_recent_donation?.amount ? (
                                            <div className="flex flex-wrap justify-between">
                                                <div>
                                                    {currencySymbol}
                                                    {maskDecimal(data.most_recent_donation?.amount)}
                                                </div>
                                                <Link
                                                    to={`/accounts/${accountId}/transactions/${data.most_recent_donation.id}`}
                                                    className="hidden text-sm font-medium text-blue-500 group-hover:block"
                                                >
                                                    View
                                                </Link>
                                            </div>
                                        ) : (
                                            '—'
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-8">
                                <div>
                                    <div className="text-sm text-gray-600">Avg. donation</div>
                                    <div className="text-base font-medium">
                                        {data.average_donation ? (
                                            <>
                                                {currencySymbol}
                                                {maskDecimal(data.average_donation)}
                                            </>
                                        ) : (
                                            '—'
                                        )}
                                    </div>
                                </div>
                                <div className="group">
                                    <div className="text-sm text-gray-600">Largest donation</div>
                                    <div className="text-base font-medium">
                                        {data.largest_donation?.amount ? (
                                            <div className="flex flex-wrap justify-between">
                                                <div>
                                                    {currencySymbol}
                                                    {maskDecimal(data.largest_donation?.amount)}
                                                </div>
                                                <Link
                                                    to={`/accounts/${accountId}/transactions/${data.largest_donation.id}`}
                                                    className="hidden text-sm font-medium text-blue-500 group-hover:block"
                                                >
                                                    View
                                                </Link>
                                            </div>
                                        ) : (
                                            '—'
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Box>
    );
};

export { ContactHouseholdChart };
